import { useEffect } from 'react';

import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

import zIndex from 'consts/zIndex';

const TIMEOUT_DELAY = 300;
const MODAL_ROOT_ID = 'modal-root';

export default function Modal({ display, handleHide, children }) {
  const el =
    document.getElementById(MODAL_ROOT_ID) || document.createElement('div');

  useEffect(() => {
    if (!el.id) {
      el.setAttribute('id', MODAL_ROOT_ID);
      document.body.appendChild(el);
    }

    return () => {
      if (document.body.contains(el)) document.body.removeChild(el);
    };
  }, [el]);

  return (
    display &&
    createPortal(
      <ModalOverlay onClick={handleHide}>
        <Fade timeout={TIMEOUT_DELAY}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <Message className="popup-error" data-testid="error-modal">
              {children}
            </Message>
            <CloseButton onClick={handleHide}>×</CloseButton>
          </ModalContainer>
        </Fade>
      </ModalOverlay>,
      el
    )
  );
}

Modal.propTypes = {
  display: PropTypes.bool,
  handleHide: PropTypes.func.isRequired,
  children: PropTypes.node,
};

Modal.defaultProps = {
  display: false,
};

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const Fade = styled.div`
  animation: ${fadeIn} ${props => (props.timeout / 1000).toFixed(2)}s linear;
  transition: visibility ${props => (props.timeout / 1000).toFixed(2)}s linear;
`;

const ModalOverlay = styled.div`
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  position: fixed;
  z-index: ${zIndex.errorModal};
  height: 0;
  line-height: 1.6;
`;

const ModalContainer = styled.div`
  font-family: ${props => props.theme.fonts.primaryFont};
  position: absolute;
  left: 0;
  right: 0;
  font-size: 13px;
  font-weight: 300;
  width: 90%;
  min-width: auto;
  max-width: 400px;
  margin: 15px auto auto;
  padding: 12px 40px 12px 20px;
  text-align: center;
  color: ${props => props.theme.colors.chestnutRose};
  background-color: ${props => props.theme.colors.bridesmaid};
  border: 1.2px solid ${props => props.theme.colors.azalea};
  border-radius: 3px;
  display: block;
`;

const Message = styled.p`
  margin: 0;
  span {
    white-space: pre-wrap;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  padding: 5px 10px;
  cursor: pointer;
  transition: all 0.3s;
  height: 51px;
  line-height: 26px;
  color: inherit;
  opacity: 0.3;
  font-size: 23px;
  font-weight: 400;

  &:hover {
    opacity: 0.7;
  }
`;
